import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import ImageUpload from '../components/ImageUpload';
import { orderService, emailService } from '../services/api';
import { toast } from '../hooks/use-toast';
import { 
  CheckCircle, Clock, ArrowRight, Package, Truck, Wrench, 
  Home, Mail, Save, Image as ImageIcon, X, Paperclip, FileText, Upload, Check
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OrderTracking = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({
    to_email: '',
    subject: '',
    body: '',
    sent_by: '',
    attachments: []
  });
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [emailHistory, setEmailHistory] = useState([]);
  const [loadingEmailHistory, setLoadingEmailHistory] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imageCaption, setImageCaption] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [stageNotes, setStageNotes] = useState('');

  const stages = [
    { key: 'pickup_scheduled', label: 'Pick up Scheduled', icon: Clock, color: 'blue' },
    { key: 'picked_up', label: 'Picked Up', icon: Truck, color: 'blue' },
    { key: 'reached_garage', label: 'Reached Garage', icon: Package, color: 'purple' },
    { key: 'ongoing', label: 'Ongoing', icon: Wrench, color: 'orange' },
    { key: 'drop_scheduled', label: 'Drop Scheduled', icon: Clock, color: 'blue' },
    { key: 'delivered', label: 'Delivered', icon: Home, color: 'green' }
  ];

  useEffect(() => {
    if (orderId) {
      fetchOrder();
      fetchEmailHistory();
    }
  }, [orderId]);

  const fetchEmailHistory = async () => {
    try {
      setLoadingEmailHistory(true);
      const response = await fetch(`${BACKEND_URL}/api/email-communications/order/${orderId}`);
      const data = await response.json();
      
      if (data.success) {
        setEmailHistory(data.communications || []);
      }
    } catch (error) {
      console.error('Error fetching email history:', error);
    } finally {
      setLoadingEmailHistory(false);
    }
  };

  // Debug: Log order images when they change
  useEffect(() => {
    if (order?.images) {
      console.log('[OrderTracking] Order images:', order.images);
      console.log('[OrderTracking] BACKEND_URL:', BACKEND_URL);
    }
  }, [order?.images]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const data = await orderService.getById(orderId);
      setOrder(data.order);
      setEmailForm({ ...emailForm, to_email: data.order.customer.email || '' });
    } catch (error) {
      console.error('Error fetching order:', error);
      toast({
        title: 'Error',
        description: 'Failed to load order',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStageUpdate = async (newStage) => {
    try {
      await orderService.updateStage(orderId, newStage, stageNotes);
      toast({
        title: 'Success',
        description: `Order stage updated`
      });
      setStageNotes('');
      fetchOrder();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update stage',
        variant: 'destructive'
      });
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setEmailSending(true);

    try {
      // Send email using new communications endpoint
      const response = await fetch(`${BACKEND_URL}/api/email-communications/send?order_id=${order.order_id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to_email: order.customer.email,
          subject: emailForm.subject,
          body: emailForm.body,
          attachments: emailForm.attachments,
          sent_by: emailForm.sent_by || 'Admin'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        toast({
          title: 'Email Sent!',
          description: 'Email sent successfully to customer'
        });
        setEmailDialogOpen(false);
        setEmailForm({
          to_email: order.customer.email || '',
          subject: '',
          body: '',
          sent_by: '',
          attachments: []
        });
        setAttachmentFiles([]);
        // Refresh email history
        fetchEmailHistory();
      } else {
        toast({
          title: 'Error',
          description: data.message || 'Failed to send email',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to send email. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setEmailSending(false);
    }
  };

  const handleImageUpload = async () => {
    const images = Array.isArray(selectedImages) ? selectedImages : (selectedImages ? [selectedImages] : []);
    
    if (images.length === 0) {
      toast({
        title: 'No Images Selected',
        description: 'Please select at least one image to upload',
        variant: 'destructive'
      });
      return;
    }

    try {
      setUploadingImage(true);

      let successCount = 0;
      let failCount = 0;

      // Upload each image separately
      for (const image of images) {
        try {
          const formData = new FormData();
          formData.append('file', image);
          formData.append('order_id', orderId);
          formData.append('caption', imageCaption);

          const response = await axios.post(
            `${BACKEND_URL}/api/upload-image`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }
          );

          if (response.data.success) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (err) {
          console.error('Error uploading image:', err);
          failCount++;
        }
      }

      // Show result toast
      if (successCount > 0) {
        toast({
          title: 'Success',
          description: `${successCount} image${successCount > 1 ? 's' : ''} uploaded successfully${failCount > 0 ? `, ${failCount} failed` : ''}`
        });
      } else {
        toast({
          title: 'Upload Failed',
          description: 'Failed to upload images',
          variant: 'destructive'
        });
      }

      setImageDialogOpen(false);
      setSelectedImages([]);
      setImageCaption('');
      fetchOrder(); // Refresh order to show new images
    } catch (error) {
      console.error('Error uploading images:', error);
      toast({
        title: 'Upload Failed',
        description: 'Failed to upload images',
        variant: 'destructive'
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAddImage = async (e) => {
    e.preventDefault();
    
    if (!imageForm.image_url) {
      toast({
        title: 'Validation Error',
        description: 'Please enter an image URL',
        variant: 'destructive'
      });
      return;
    }

    try {
      await orderService.addImage(orderId, imageForm.image_url, imageForm.caption);
      toast({
        title: 'Success',
        description: 'Image added successfully'
      });
      setImageDialogOpen(false);
      setImageForm({ image_url: '', caption: '' });
      fetchOrder();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to add image',
        variant: 'destructive'
      });
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingAttachment(true);
    
    try {
      const uploadedUrls = [];
      
      for (const file of files) {
        // Check file size (max 5MB per file)
        if (file.size > 5 * 1024 * 1024) {
          toast({
            title: 'File too large',
            description: `${file.name} exceeds 5MB limit`,
            variant: 'destructive'
          });
          continue;
        }

        // Upload file
        const formData = new FormData();
        formData.append('file', file);
        formData.append('order_id', order.order_id);
        formData.append('caption', `Email attachment: ${file.name}`);

        const response = await fetch(`${BACKEND_URL}/api/upload-image`, {
          method: 'POST',
          body: formData
        });

        const data = await response.json();
        
        if (data.success) {
          uploadedUrls.push({
            name: file.name,
            url: `${BACKEND_URL}${data.image_url}`,
            size: file.size
          });
        }
      }

      if (uploadedUrls.length > 0) {
        setAttachmentFiles([...attachmentFiles, ...uploadedUrls]);
        setEmailForm({
          ...emailForm,
          attachments: [...emailForm.attachments, ...uploadedUrls.map(f => f.url)]
        });
        
        toast({
          title: 'Files uploaded',
          description: `${uploadedUrls.length} file(s) uploaded successfully`
        });
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      toast({
        title: 'Upload failed',
        description: 'Failed to upload files. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setUploadingAttachment(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleRemoveAttachment = (index) => {
    setAttachmentFiles(attachmentFiles.filter((_, i) => i !== index));
    setEmailForm({
      ...emailForm,
      attachments: emailForm.attachments.filter((_, i) => i !== index)
    });
  };

  const getCurrentStageIndex = () => {
    return stages.findIndex(s => s.key === order?.current_stage);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-gray-600">Loading order...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <p className="text-gray-600">Order not found</p>
            <Button onClick={() => navigate('/admin')} className="mt-4">
              Back to Admin
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentStageIndex = getCurrentStageIndex();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8">
      <div className="container mx-auto px-4">
        <div className="mb-6">
          <Button variant="outline" onClick={() => navigate('/admin')}>
            ← Back to Admin
          </Button>
        </div>

        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Order #{order.order_id}</h1>
                <p className="text-gray-600">Booking ID: {order.booking_id}</p>
              </div>
              <Badge className="bg-blue-600 text-lg px-4 py-2">
                {stages.find(s => s.key === order.current_stage)?.label}
              </Badge>
            </div>
            <div className="grid md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Customer</p>
                <p className="font-semibold">{order.customer.name}</p>
                <p className="text-gray-600">{order.customer.mobile}</p>
                {order.customer.email && (
                  <p className="text-xs text-gray-500">{order.customer.email}</p>
                )}
              </div>
              <div>
                <p className="text-gray-600">Vehicle</p>
                <p className="font-semibold">{order.car_details.brand} {order.car_details.model}</p>
                {order.car_details.fuel_type && (
                  <p className="text-sm text-gray-600">{order.car_details.fuel_type}</p>
                )}
                {order.car_details.manufacturing_year && (
                  <p className="text-sm text-gray-600">{order.car_details.manufacturing_year}</p>
                )}
              </div>
              <div>
                <p className="text-gray-600">Service</p>
                <p className="font-semibold">{order.service.service_name}</p>
                <p className="text-gray-600">{order.service.package_name}</p>
                <p className="text-blue-600 font-semibold mt-1">₹{order.service.price}</p>
              </div>
              <div>
                <p className="text-gray-600">Garage Assigned</p>
                <p className="font-semibold">{order.garage_name || 'Not Assigned'}</p>
                {order.garage_id && (
                  <p className="text-xs text-gray-500">ID: {order.garage_id}</p>
                )}
              </div>
            </div>
            
            {/* Additional Order Details */}
            <div className="mt-4 pt-4 border-t grid md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-600 font-medium mb-2">Pickup Schedule</p>
                {order.schedule && order.schedule.date ? (
                  <div>
                    <p className="text-gray-900 font-semibold">
                      {new Date(order.schedule.date).toLocaleDateString('en-IN', {
                        weekday: 'short',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </p>
                    {order.schedule.time_slot && (
                      <p className="text-gray-700 text-xs mt-1">
                        Time: {order.schedule.time_slot}
                      </p>
                    )}
                  </div>
                ) : order.pickup_scheduled_at ? (
                  <p className="text-gray-900">
                    {new Date(order.pickup_scheduled_at).toLocaleString('en-IN', {
                      dateStyle: 'medium',
                      timeStyle: 'short'
                    })}
                  </p>
                ) : (
                  <p className="text-gray-500 italic">Not scheduled yet</p>
                )}
              </div>
              <div>
                <p className="text-gray-600 font-medium mb-2">Customer Voice/Concern</p>
                {order.customer_concern || order.special_requirements ? (
                  <p className="text-gray-900">{order.customer_concern || order.special_requirements}</p>
                ) : (
                  <p className="text-gray-500 italic">No specific concerns mentioned</p>
                )}
              </div>
              <div>
                <p className="text-gray-600 font-medium mb-2">Created On</p>
                {order.created_at && (
                  <p className="text-gray-900">
                    {new Date(order.created_at).toLocaleString('en-IN', {
                      dateStyle: 'medium',
                      timeStyle: 'short'
                    })}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Order Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stages.map((stage, index) => {
                    const StageIcon = stage.icon;
                    const isCompleted = index < currentStageIndex;
                    const isCurrent = index === currentStageIndex;

                    return (
                      <div key={stage.key} className="relative">
                        {index < stages.length - 1 && (
                          <div className={`absolute left-6 top-12 w-0.5 h-16 ${isCompleted ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        )}
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isCompleted ? 'bg-green-500' :
                            isCurrent ? 'bg-blue-600' :
                            'bg-gray-300'
                          }`}>
                            {isCompleted ? (
                              <CheckCircle className="w-6 h-6 text-white" />
                            ) : (
                              <StageIcon className="w-6 h-6 text-white" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h3 className={`font-semibold ${isCurrent ? 'text-blue-600' : 'text-gray-900'}`}>
                                {stage.label}
                              </h3>
                              {isCurrent && index < stages.length - 1 && (
                                <Button 
                                  size="sm" 
                                  onClick={() => handleStageUpdate(stages[index + 1].key)}
                                >
                                  Complete <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                              )}
                            </div>
                            {order.stage_history.filter(h => h.stage === stage.key).map((history, idx) => (
                              <div key={idx} className="mt-2 p-3 bg-gray-50 rounded text-sm">
                                <p className="text-gray-600">
                                  {new Date(history.timestamp).toLocaleString('en-IN', {
                                    dateStyle: 'medium',
                                    timeStyle: 'short'
                                  })}
                                </p>
                                {history.notes && <p className="text-gray-700 mt-1">{history.notes}</p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {currentStageIndex < stages.length - 1 && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <Label className="text-sm font-medium">Add Notes (Optional)</Label>
                    <Textarea
                      value={stageNotes}
                      onChange={(e) => setStageNotes(e.target.value)}
                      placeholder="Add notes before moving to next stage..."
                      className="mt-2"
                      rows={2}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Vehicle Images Section */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Vehicle Images & Work Progress</span>
                  <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm">
                        <ImageIcon className="w-4 h-4 mr-2" />
                        Add Image
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Add Vehicle Image</DialogTitle>
                        <p className="text-sm text-gray-600">Upload from device or take a photo using camera</p>
                      </DialogHeader>
                      <div className="space-y-4">
                        <ImageUpload 
                          onImageSelect={setSelectedImages}
                          maxSize={10}
                          multiple={true}
                        />
                        
                        <div>
                          <Label>Caption (Optional)</Label>
                          <Input
                            value={imageCaption}
                            onChange={(e) => setImageCaption(e.target.value)}
                            placeholder="e.g., Before service, Work in progress, After repair"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            This caption will be applied to all uploaded images
                          </p>
                        </div>

                        <div className="flex justify-end gap-2">
                          <Button 
                            type="button" 
                            variant="outline" 
                            onClick={() => {
                              setImageDialogOpen(false);
                              setSelectedImages([]);
                              setImageCaption('');
                            }}
                          >
                            Cancel
                          </Button>
                          <Button 
                            onClick={handleImageUpload}
                            disabled={!selectedImages || (Array.isArray(selectedImages) ? selectedImages.length === 0 : !selectedImages) || uploadingImage}
                          >
                            {uploadingImage ? 'Uploading...' : (
                              <>
                                <Save className="w-4 h-4 mr-2" /> 
                                Upload Image
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {order.images && order.images.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    {order.images.map((img, idx) => {
                      // Construct the full image URL
                      const imageUrl = img.image_url || (img.url ? `${BACKEND_URL}${img.url}` : null);
                      
                      return (
                        <div key={idx} className="border rounded-lg overflow-hidden hover:shadow-lg transition-all">
                          {imageUrl ? (
                            <img 
                              src={imageUrl} 
                              alt={img.caption || 'Vehicle'} 
                              className="w-full h-48 object-cover cursor-pointer"
                              onClick={() => window.open(imageUrl, '_blank')}
                              onError={(e) => {
                                console.error('Image failed to load:', imageUrl);
                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3EImage not found%3C/text%3E%3C/svg%3E';
                              }}
                            />
                          ) : (
                            <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                              <p className="text-gray-500">No image URL</p>
                            </div>
                          )}
                          {img.caption && (
                            <div className="p-3 bg-gray-50">
                              <p className="text-sm text-gray-700 font-medium">{img.caption}</p>
                              {img.uploaded_at && (
                                <p className="text-xs text-gray-500 mt-1">
                                  {new Date(img.uploaded_at).toLocaleString()}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500">No images uploaded yet</p>
                    <p className="text-sm text-gray-400 mt-1">Add images to track work progress</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Email Customer</span>
                  <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm">
                        <Mail className="w-4 h-4 mr-2" />
                        Compose
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Send Email to Customer</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleSendEmail} className="space-y-4">
                        <div>
                          <Label>To</Label>
                          <Input
                            type="email"
                            value={emailForm.to_email}
                            onChange={(e) => setEmailForm({ ...emailForm, to_email: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <Label>From (Your Name)</Label>
                          <Input
                            value={emailForm.sent_by}
                            onChange={(e) => setEmailForm({ ...emailForm, sent_by: e.target.value })}
                            placeholder="Your name"
                            required
                          />
                        </div>
                        <div>
                          <Label>Subject</Label>
                          <Input
                            value={emailForm.subject}
                            onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                            placeholder="Order update"
                            required
                          />
                        </div>
                        <div>
                          <Label>Message</Label>
                          <Textarea
                            value={emailForm.body}
                            onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                            rows={6}
                            placeholder="Your message..."
                            required
                          />
                        </div>
                        <div>
                          <Label className="flex items-center justify-between mb-2">
                            <span>Attachments (Images/Files)</span>
                            <label className="cursor-pointer">
                              <Input
                                type="file"
                                multiple
                                accept="image/*,.pdf,.doc,.docx"
                                onChange={handleFileSelect}
                                className="hidden"
                                disabled={uploadingAttachment}
                              />
                              <Button 
                                type="button" 
                                size="sm" 
                                variant="outline"
                                disabled={uploadingAttachment}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.currentTarget.previousElementSibling.click();
                                }}
                              >
                                {uploadingAttachment ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-600 border-t-transparent mr-2" />
                                    Uploading...
                                  </>
                                ) : (
                                  <>
                                    <Upload className="w-4 h-4 mr-2" />
                                    Upload Files
                                  </>
                                )}
                              </Button>
                            </label>
                          </Label>
                          
                          {attachmentFiles.length > 0 && (
                            <div className="mt-2 space-y-2">
                              {attachmentFiles.map((file, index) => (
                                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                                    <Paperclip className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium truncate">{file.name}</p>
                                      <p className="text-xs text-gray-500">
                                        {(file.size / 1024).toFixed(1)} KB
                                      </p>
                                    </div>
                                  </div>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleRemoveAttachment(index)}
                                    className="flex-shrink-0"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          <p className="text-xs text-gray-500 mt-2">
                            Max 5MB per file. Supported: Images, PDF, DOC
                          </p>
                          
                          {/* Option to select from existing order images */}
                          {order.images && order.images.length > 0 && (
                            <div className="mt-4 pt-4 border-t">
                              <p className="text-sm font-medium mb-2">Or select from order images:</p>
                              <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                                {order.images.map((img, idx) => {
                                  const imageUrl = img.image_url || (img.url ? `${BACKEND_URL}${img.url}` : null);
                                  const isSelected = emailForm.attachments.includes(imageUrl);
                                  
                                  return (
                                    <div
                                      key={idx}
                                      onClick={() => {
                                        if (isSelected) {
                                          // Remove from attachments
                                          const fileIndex = attachmentFiles.findIndex(f => f.url === imageUrl);
                                          if (fileIndex > -1) {
                                            handleRemoveAttachment(fileIndex);
                                          }
                                        } else {
                                          // Add to attachments
                                          setAttachmentFiles([...attachmentFiles, {
                                            name: img.caption || `Order image ${idx + 1}`,
                                            url: imageUrl,
                                            size: 0
                                          }]);
                                          setEmailForm({
                                            ...emailForm,
                                            attachments: [...emailForm.attachments, imageUrl]
                                          });
                                        }
                                      }}
                                      className={`relative cursor-pointer border-2 rounded transition-all ${
                                        isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-blue-300'
                                      }`}
                                    >
                                      <img
                                        src={imageUrl || '/placeholder.png'}
                                        alt={img.caption || 'Order'}
                                        className="w-full h-20 object-cover rounded"
                                      />
                                      {isSelected && (
                                        <div className="absolute inset-0 bg-blue-500 bg-opacity-20 rounded flex items-center justify-center">
                                          <Check className="w-6 h-6 text-blue-600" />
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="bg-blue-50 p-3 rounded text-sm">
                          <p className="text-blue-800">✓ Emails are now being sent via Zoho (support@propitstop.com)</p>
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button type="button" variant="outline" onClick={() => {
                            setEmailDialogOpen(false);
                            setAttachmentFiles([]);
                          }}>
                            Cancel
                          </Button>
                          <Button type="submit" disabled={emailSending}>
                            {emailSending ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                                Sending...
                              </>
                            ) : (
                              <>
                                <Mail className="w-4 h-4 mr-2" /> Send Email
                              </>
                            )}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">Send updates to customer about order progress</p>
              </CardContent>
            </Card>

            {/* Email Communications History */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Email Communications History</span>
                  <Badge variant="secondary">{emailHistory.length} emails</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingEmailHistory ? (
                  <div className="text-center py-4">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
                    <p className="mt-2 text-sm text-gray-600">Loading email history...</p>
                  </div>
                ) : emailHistory.length === 0 ? (
                  <div className="text-center py-8">
                    <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No emails sent yet</p>
                    <p className="text-xs text-gray-400 mt-1">Email communications will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {emailHistory.map((email, index) => (
                      <div key={index} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Mail className="w-4 h-4 text-blue-600" />
                              <span className="font-semibold text-gray-900">{email.subject}</span>
                            </div>
                            <p className="text-xs text-gray-600">
                              To: {email.to_email} • From: {email.sent_by || email.from_email}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-500">
                              {new Date(email.timestamp).toLocaleDateString('en-IN', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(email.timestamp).toLocaleTimeString('en-IN', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="mt-3 p-3 bg-white rounded border text-sm text-gray-700">
                          <div dangerouslySetInnerHTML={{ __html: email.body }} />
                        </div>
                        {email.attachments && email.attachments.length > 0 && (
                          <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                            <Paperclip className="w-3 h-3" />
                            <span>{email.attachments.length} attachment(s)</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Order Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={order.notes || ''}
                  onChange={async (e) => {
                    const newNotes = e.target.value;
                    try {
                      await orderService.updateNotes(orderId, newNotes);
                      setOrder({ ...order, notes: newNotes });
                    } catch (error) {
                      console.error('Failed to update notes');
                    }
                  }}
                  placeholder="Internal notes about this order..."
                  rows={6}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;
